import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import myshieldLogo from '../../assets/myshield-logo.png';

/**
 * Full-screen blocking gate shown to any non-SuperAdmin user who has not
 * completed their Academy training. The user cannot dismiss or bypass it —
 * the only action is to navigate to the Academy.
 */
const AcademyTrainingGate: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'linear-gradient(135deg, #001233 0%, #002060 100%)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '24px',
    }}>
      {/* Logo */}
      <img
        src={myshieldLogo}
        alt="Myshield"
        style={{ width: 120, marginBottom: 28, opacity: 0.92 }}
      />

      {/* Icon */}
      <div style={{
        width: 80, height: 80, borderRadius: '50%',
        background: 'rgba(255,165,0,0.15)',
        border: '2px solid rgba(255,165,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 40, marginBottom: 24,
      }}>
        🎓
      </div>

      {/* Heading */}
      <h1 style={{
        color: '#ffffff', fontSize: 26, fontWeight: 800,
        textAlign: 'center', margin: '0 0 12px',
        maxWidth: 480,
      }}>
        Training Required Before Access
      </h1>

      {/* Body */}
      <p style={{
        color: 'rgba(255,255,255,0.78)', fontSize: 16, lineHeight: 1.7,
        textAlign: 'center', maxWidth: 520, margin: '0 0 10px',
      }}>
        Hello <strong style={{ color: '#fff' }}>{user?.firstName}</strong> — before you can
        access the Myshield portal, you must complete your Academy training programme.
      </p>
      <p style={{
        color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 1.65,
        textAlign: 'center', maxWidth: 500, margin: '0 0 32px',
      }}>
        Training is <strong style={{ color: '#FFA500' }}>compulsory for all users</strong> and
        covers everything you need to manage your fleet correctly and in line with policy
        requirements. Once all modules are complete, full access to the portal is unlocked
        automatically.
      </p>

      {/* Info tiles */}
      <div style={{
        display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center',
        marginBottom: 36, maxWidth: 560,
      }}>
        {[
          { icon: '📚', label: '5 short modules' },
          { icon: '✅', label: 'Quiz after each module' },
          { icon: '🔓', label: 'Full access on completion' },
        ].map(({ icon, label }) => (
          <div key={label} style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 10, padding: '12px 20px',
            display: 'flex', alignItems: 'center', gap: 10,
            color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 600,
          }}>
            <span style={{ fontSize: 20 }}>{icon}</span>
            {label}
          </div>
        ))}
      </div>

      {/* CTA */}
      <button
        onClick={() => navigate('/academy')}
        style={{
          background: '#FFA500', color: '#001233',
          border: 'none', borderRadius: 10,
          padding: '16px 48px', fontSize: 17, fontWeight: 800,
          cursor: 'pointer', letterSpacing: 0.3,
          boxShadow: '0 4px 20px rgba(255,165,0,0.35)',
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 28px rgba(255,165,0,0.45)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.transform = '';
          (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 20px rgba(255,165,0,0.35)';
        }}
      >
        Begin My Training →
      </button>

      <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 24, textAlign: 'center' }}>
        Myshield — A member of the Yourshield Family &nbsp;·&nbsp; FRN: 617848
      </p>
    </div>
  );
};

export default AcademyTrainingGate;
